import React from 'react';
import { Redirect, Route } from 'react-router-dom';
import { Box } from '@amzn/awsui-components-react';
import { AppModules } from 'src/models/permissions/RolePermissions';
import CONSTANTS from 'src/utils/constants';
import AgreementDetails from './agreements/AgreementDetails';
import AgreementsView from './agreements/AgreementsView';
import CalculationBuilderView from './calculation-builder/CalculationBuilderView';
import BulkActionsView from './calculations/BulkActionsView';
import CalculationsLanding from './calculations/CalculationsLanding';
import ReadOnlyCalculationView from './calculations/ReadOnlyCalculationView';
import SearchCalculations from './calculations/SearchCalculations';
import AllApprovalsAndRequestsView from './dashboard/AllApprovalsAndRequestsView';
import AllCalculationReviewsView from './dashboard/AllCalculationReviewsView';
import AllTasksView from './dashboard/AllTasksView';
import DashboardView from './dashboard/DashboardView';
import ICAccountMappingView from './ic-account-mapping/ICAccountMappingView';
import MECView from './mec/MECView';
import PeriodChargesView from './period-charges/PeriodChargesView';
import TPTaxCwbReportView from './reports/cwbReport/TPTaxCwbReportView';
import TPTaxLefReportView from './reports/lefReport/TPTaxLefReportView';
import ReportsTabbedView from './reports/prevalidation/ReportsTabbedView';
import ReportDashboard from './reports/reportDashboard/ReportDashboard';
import ReportingHistoryTableView from './reports/ReportingHistoryTableView';
import TaxAuditRecordDetailsView from './reports/taxAuditRecord/TaxAuditRecordDetailsView';
import TaxAuditRecordsView from './reports/taxAuditRecord/TaxAuditRecordsView';
import TPTaxReportView from './reports/TPTaxReportView';
import TPTaxVarianceReportView from './reports/tpTaxVarianceReportDashboard/TPTaxVarianceReportView';
import TestedPartyMappingView from './tested-party-mapping/TestedPartyMappingView';
import { ATPViewRestrictor } from './shared/ATPViewRestrictor';
import CloneCalculationsView from './templates/link/CloneCalculationsView';
import LinkToTemplateView from './templates/link/LinkToTemplateView';
import TPAllocationView from './tp-allocation/TPAllocatonView';
import WorksheetTemplateView from './tp-allocation/worksheet-templates/WorksheetTemplateView';
import LinkedWorksheetView from './tp-allocation/worksheets/LinkedWorksheetView';
import StandaloneWorksheetView from './tp-allocation/worksheets/StandaloneWorksheetView';
import CustomTablesView from "src/components/custom-tables/TablesView";
import CustomCOAView from "src/components/custom-coa/TablesView";
import CustomTableRecordsView from 'src/components/custom-tables/RecordsView';
import CustomCOARowsView from 'src/components/custom-coa/RowsView';
import ServiceCollection from 'src/services/ServiceCollection';
import { UserProfile } from 'src/models/users/UserProfile';
import LinkedJEWorksheetView from "src/components/jeAllocation/worksheet/LinkedJEWorksheetView";
import JEWorksheetTemplateView from "src/components/jeAllocation/worksheet-template/JEWorksheetTemplateView";
import StandaloneJEWorksheetView from "src/components/jeAllocation/worksheet/StandaloneJEWorksheetView";
import JEAllocationView from "src/components/jeAllocation/JEAllocatonView";
import ConfigureJournalsView from "src/components/calculation-builder/configure-journals/ConfigureJournalsView";
import MultidimensionalAllocationView from './multidimensional-allocation/MultidimensionalAllocationView';
import MultidimensionalAllocationCalculationView from "src/components/multidimensional-allocation/MultidimensionalAllocationCalculationView";
import MDASheetView from './multidimensional-allocation/MDASheetView';
import ReportAnalysis from './report-analysis/ReportAnalysis';

const REPORT_ANALYSIS_ALLOWED_USERS = CONSTANTS.PAGE_NAV.REPORT_ANALYSIS.ALLOWED_USERS;
const IS_LOCAL = window.location.hostname === 'localhost';
const IS_REPORT_ANALYSIS_ENABLED = IS_LOCAL || CONSTANTS.ENVIRONMENT_VARIABLES.TPE_UI_STAGE === 'gamma';

export default function getRoutes(serviceCollection: ServiceCollection, userProfile: UserProfile) {
    return [
        <Route exact path={CONSTANTS.PAGE_NAV.DASHBOARD.URL}>
            <ATPViewRestrictor module={AppModules.DASHBOARD}><DashboardView /></ATPViewRestrictor>
        </Route>,
        <Route exact path={CONSTANTS.PAGE_NAV.APPROVALS_AND_REQUESTS.URL}>
            <ATPViewRestrictor module={AppModules.DASHBOARD}><AllApprovalsAndRequestsView /></ATPViewRestrictor>
        </Route>,
        <Route exact path={CONSTANTS.PAGE_NAV.TASKS.URL}>
            <ATPViewRestrictor module={AppModules.DASHBOARD}><AllTasksView /></ATPViewRestrictor>
        </Route>,
        <Route exact path={CONSTANTS.PAGE_NAV.CALCULATION_REVIEWS.URL}>
            <ATPViewRestrictor module={AppModules.DASHBOARD}><AllCalculationReviewsView/></ATPViewRestrictor>
        </Route>,
        <Route exact path="/reports">
            <ATPViewRestrictor module={AppModules.REPORTING}><ReportDashboard
                services={serviceCollection}/></ATPViewRestrictor>
        </Route>,
        ...(IS_REPORT_ANALYSIS_ENABLED && REPORT_ANALYSIS_ALLOWED_USERS.includes(`${userProfile?.user || ''}@amazon.com`)
            ? [<Route exact path={CONSTANTS.PAGE_NAV.REPORT_ANALYSIS.URL}>
                <ReportAnalysis />
            </Route>]
            : []),
        <Route exact path="/reports/history">
            <ATPViewRestrictor module={AppModules.REPORTING}><ReportingHistoryTableView
                services={serviceCollection}/></ATPViewRestrictor>
        </Route>,
        <Route exact path="/reports/taxAuditRecord">
            <ATPViewRestrictor module={AppModules.REPORTING}><TaxAuditRecordsView
                services={serviceCollection}/></ATPViewRestrictor>
        </Route>,
        <Route exact path="/reports/tpTaxVarianceRecords">
            <ATPViewRestrictor module={AppModules.REPORTING}><TPTaxVarianceReportView
                services={serviceCollection}/></ATPViewRestrictor>
        </Route>,
        ...(CONSTANTS.ENVIRONMENT_VARIABLES.TPE_UI_STAGE !== 'prod'
            ? [<Route exact path={CONSTANTS.PAGE_NAV.TESTED_PARTY_MAPPING.URL}>
                <ATPViewRestrictor module={AppModules.CALCULATION_MANAGER}>
                    <TestedPartyMappingView services={serviceCollection}/>
                </ATPViewRestrictor>
            </Route>]
            : []),
        <Route exact path="/reports/taxAuditRecord/details/:encodedTransactionId&:encodedTaxJournalHeaderId">
            <ATPViewRestrictor module={AppModules.REPORTING}><TaxAuditRecordDetailsView services={serviceCollection}/></ATPViewRestrictor>
        </Route>,
        <Route exact path="/agreements">
            <ATPViewRestrictor module={AppModules.TP_WORKLISTS}><AgreementsView  {...{ services: serviceCollection }} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/agreements/details/:encodedRecordId">
            <ATPViewRestrictor module={AppModules.TP_WORKLISTS}><AgreementDetails services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations">
            <ATPViewRestrictor module={AppModules.CALCULATION_MANAGER}><CalculationsLanding /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/search">
            <ATPViewRestrictor module={AppModules.SEARCH_CALCULATION}>
                <SearchCalculations {...{ services: serviceCollection, userProfile: userProfile as UserProfile }} />
            </ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/builder/:encodedCliRecordId">
            <ATPViewRestrictor module={AppModules.CALCULATION_BUILDER}>
                <CalculationBuilderView {...{ services: serviceCollection, isICRSRecord: false }} />
            </ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/builder/configureJournals/:encodedCliRecordId">
            <ATPViewRestrictor module={AppModules.CALCULATION_BUILDER}>
                <ConfigureJournalsView {...{ services: serviceCollection, isICRSRecord: false }} />
            </ATPViewRestrictor>
        </Route>,
        <Route exact path="/icrs-calculations/builder/:encodedCliRecordId">
            <ATPViewRestrictor module={AppModules.CALCULATION_BUILDER}>
                <CalculationBuilderView {...{ services: serviceCollection, isICRSRecord: true }} />
            </ATPViewRestrictor>
        </Route>,
        <Route exact path="/icrs-calculations/builder/configureJournals/:encodedCliRecordId">
            <ATPViewRestrictor module={AppModules.CALCULATION_BUILDER}>
                <ConfigureJournalsView {...{ services: serviceCollection, isICRSRecord: true }} />
            </ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/readOnly">
            <ATPViewRestrictor module={AppModules.READ_ONLY_CALC}><ReadOnlyCalculationView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/link">
            <ATPViewRestrictor module={AppModules.BULK_LINK}><LinkToTemplateView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/clone">
            <ATPViewRestrictor module={AppModules.BULK_CLONE}><CloneCalculationsView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/customDataTables">
            <ATPViewRestrictor module={AppModules.CUSTOM_DATA_TABLES}><CustomTablesView services={serviceCollection }/></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/customDataTables/details/:encodedTableId">
            <ATPViewRestrictor module={AppModules.CUSTOM_DATA_TABLES}><CustomTableRecordsView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/customCoa">
            <ATPViewRestrictor module={AppModules.CUSTOM_COA}><CustomCOAView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/customCoa/details/:encodedTableId">
            <ATPViewRestrictor module={AppModules.CUSTOM_COA}><CustomCOARowsView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/tpAllocation">
            <ATPViewRestrictor module={AppModules.TP_ALLOCATION}><TPAllocationView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/tpAllocation/standalone/:encodedWorksheetId">
            <ATPViewRestrictor module={AppModules.TP_ALLOCATION}><StandaloneWorksheetView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/tpAllocation/linked/:encodedWorksheetId">
            <ATPViewRestrictor module={AppModules.TP_ALLOCATION}><LinkedWorksheetView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/tpAllocation/template/:encodedTemplateId">
            <ATPViewRestrictor module={AppModules.TP_ALLOCATION}><WorksheetTemplateView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/jeAllocation">
            <ATPViewRestrictor module={AppModules.JE_ALLOCATION}><JEAllocationView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/jeAllocation/standalone/:encodedWorksheetId">
            <ATPViewRestrictor module={AppModules.JE_ALLOCATION}><StandaloneJEWorksheetView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/jeAllocation/linked/:encodedWorksheetId">
            <ATPViewRestrictor module={AppModules.JE_ALLOCATION}><LinkedJEWorksheetView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/jeAllocation/template/:encodedTemplateId">
            <ATPViewRestrictor module={AppModules.JE_ALLOCATION}><JEWorksheetTemplateView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/mec">
            <ATPViewRestrictor module={AppModules.MEC}><MECView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/balanceReconciliation">
            <ATPViewRestrictor module={AppModules.PERIOD_CHARGES}><PeriodChargesView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/reports/tpTaxReport/:filter?">
            <ATPViewRestrictor module={AppModules.REPORTING}><TPTaxReportView services={serviceCollection}/></ATPViewRestrictor>
        </Route>,
        <Route exact path="/reports/lef/:filter?">
            <ATPViewRestrictor module={AppModules.REPORTING}><TPTaxLefReportView services={serviceCollection}/></ATPViewRestrictor>
        </Route>,
        <Route exact path="/reports/cwb/:filter?">
            <ATPViewRestrictor module={AppModules.REPORTING}><TPTaxCwbReportView services={serviceCollection}/></ATPViewRestrictor>
        </Route>,
        <Route exact path="/reports/prevalidation">
            <ATPViewRestrictor module={AppModules.REPORTING}><ReportsTabbedView services={serviceCollection}/></ATPViewRestrictor>
        </Route>,
        <Route exact path={"/icAccountMapping"}>
            <Redirect to={"/icAccountMapping/defaultMapping"} />
        </Route>,
        <Route exact path={"/icAccountMapping/:tab"}>
            <ATPViewRestrictor module={AppModules.REPORTING}><ICAccountMappingView services={serviceCollection}/></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/bulkActions">
            <ATPViewRestrictor module={AppModules.BULK_ACTIONS}><BulkActionsView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/multidimensionalAllocation">
            <ATPViewRestrictor module={AppModules.MULTIDIMENSIONAL_ALLOCATION}><MultidimensionalAllocationView services={serviceCollection} /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/multidimensionalAllocation/calculation/:worksheetId">
            <ATPViewRestrictor module={AppModules.MULTIDIMENSIONAL_ALLOCATION}><MultidimensionalAllocationCalculationView /></ATPViewRestrictor>
        </Route>,
        <Route exact path="/calculations/multidimensionalAllocation/sheet/:sheetId">
            <ATPViewRestrictor module={AppModules.MULTIDIMENSIONAL_ALLOCATION}>
                <MDASheetView services={serviceCollection} />
            </ATPViewRestrictor>
        </Route>
    ]
}
